import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import PageMeta from "@/components/PageMeta";
import { api } from "@/lib/api";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; }
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setS(null); setNotFound(false);
    api.get(`/services/${slug}`).then((r) => setS(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <SiteLayout>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-32">
          <h1 className="h-display text-4xl">Service not found.</h1>
          <Link to="/expertise" className="link-grow mt-6 inline-flex">Back to Expertise</Link>
        </div>
      </SiteLayout>
    );
  }
  if (!s) return <SiteLayout><div /></SiteLayout>;

  return (
    <SiteLayout>
      <PageMeta
        title={s.seo_title || `${s.title} | Carlton Elleker Chase`}
        description={s.seo_description || s.summary}
      />
      <article className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-10 md:pt-20" data-testid="service-detail">
        {s.department && (
          <Link to={`/expertise/${s.department.slug}`} className="inline-flex items-center gap-2 overline text-graphite/60 hover:text-[#1E3668]">
            <ArrowLeft size={14} /> {s.department.title}
          </Link>
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
          <div className="md:col-span-8">
            <p className="overline text-graphite/55">Service</p>
            <h1 className="mt-4 h-display text-[clamp(2.2rem,5.2vw,4.8rem)] leading-[1.02] text-left" data-testid="service-title">{s.title}</h1>
          </div>
          {s.department_head && (
            <div className="md:col-span-4 md:justify-self-end w-full md:max-w-[280px]" data-testid="service-head-of-department">
              <p className="overline text-graphite/55">Head of Department</p>
              <Link to={`/people/${s.department_head.slug}`} className="mt-4 flex items-center gap-4 group">
                {s.department_head.photo_url && (
                  <img src={s.department_head.photo_url} alt={s.department_head.name} className="w-14 h-14 object-cover bw-portrait flex-shrink-0" />
                )}
                <span>
                  <span className="block font-semibold leading-tight group-hover:text-[#1E3668] transition-colors">{s.department_head.name}</span>
                  {s.department_head.title && (
                    <span className="block text-xs text-graphite/55 mt-0.5">{s.department_head.title}</span>
                  )}
                </span>
              </Link>
            </div>
          )}
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-8">
            <div
              className="prose prose-cec text-graphite/90 text-lg leading-[1.85] [&_p]:mb-5 [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_li]:my-1"
              data-testid="service-body"
              dangerouslySetInnerHTML={{ __html: s.body || "" }}
            />
          </div>
          <aside className="md:col-span-4">
            {s.department && (
              <div className="mb-10 hairline-bottom pb-6">
                <p className="text-xs text-graphite/55 mb-2">Department</p>
                <Link to={`/expertise/${s.department.slug}`} className="text-lg font-semibold hover:text-[#1E3668]">{s.department.title}</Link>
              </div>
            )}
            {s.lawyers?.length > 0 && (
              <div className="mb-10" data-testid="service-lawyers">
                <p className="text-xs text-graphite/55 mb-3">Related lawyers</p>
                <ul className="mt-4 space-y-3">
                  {s.lawyers.map((l) => (
                    <li key={l.slug}>
                      <Link to={`/people/${l.slug}`} className="flex items-center gap-3 group" data-testid={`service-lawyer-${l.slug}`}>
                        {l.photo_url && <img src={l.photo_url} alt={l.name} className="w-10 h-10 object-cover bw-portrait" />}
                        <span>
                          <span className="block font-medium group-hover:text-[#1E3668]">{l.name}</span>
                          <span className="block text-xs text-graphite/55">{l.title}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {s.articles?.length > 0 && (
              <div className="mb-10" data-testid="service-articles">
                <p className="text-xs text-graphite/55 mb-3">Related articles</p>
                <ul className="mt-4 space-y-3">
                  {s.articles.map((a) => (
                    <li key={a.slug}>
                      <Link to={`/insights/${a.slug}`} className="block font-medium hover:text-[#1E3668]">
                        {a.title}
                        <span className="block text-xs text-graphite/55 mt-0.5">{formatDate(a.published_at)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>

        {s.related_services?.length > 0 && (
          <section className="mt-24 hairline-top pt-12">
            <p className="text-sm text-graphite/55 mb-4">More from {s.department?.title}</p>
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-1">
              {s.related_services.map((r) => (
                <li key={r.slug} className="hairline-bottom">
                  <Link to={`/services/${r.slug}`} className="num-list-row flex items-center justify-between py-4">
                    <span className="num-title font-medium">{r.title}</span>
                    <ArrowUpRight size={16} className="text-graphite/40 num-arrow" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </SiteLayout>
  );
}
